import React, { useState, useRef, useEffect } from "react";

// Enhanced CSS for better animations and styling
const enhancedStyles = `
  .rotate-180 {
    transform: rotate(180deg) !important;
  }
  
  .dropdown-fade-in {
    animation: fadeIn 0.15s ease-out;
  }
  
  .dropdown-fade-out {
    animation: fadeOut 0.1s ease-out;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-4px);
    }
  }
  
  .searchable-dropdown-item {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .searchable-dropdown-item:hover {
    transform: translateX(2px);
  }
  
  .searchable-dropdown-selected {
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
  }
  
  .searchable-dropdown-highlighted {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
    transform: translateX(2px);
  }
  
  .searchable-dropdown-input:focus {
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    border-color: #007bff;
  }
  
  .searchable-dropdown-main {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid #ced4da;
  }
  
  .searchable-dropdown-main:hover:not(:disabled) {
    border-color: #007bff;
    box-shadow: 0 0 0 0.1rem rgba(0, 123, 255, 0.1);
  }
  
  .searchable-dropdown-main:focus-within {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
  
  .searchable-dropdown-chevron {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .searchable-dropdown-loading {
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

// Inject the enhanced styles
if (typeof document !== 'undefined' && !document.getElementById('searchable-handover-dropdown-enhanced-styles')) {
  const style = document.createElement('style');
  style.id = 'searchable-handover-dropdown-enhanced-styles';
  style.textContent = enhancedStyles;
  document.head.appendChild(style);
}

const SearchableHandoverTypeDropdown = ({ 
  value, 
  onChange, 
  placeholder = "Select Handover Type",
  required = false,
  disabled = false,
  loading = false,
  error = false,
  errorMessage = "",
  className = "form-select select-brand w-100",
  showCount = true,
  maxHeight = "300px"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Define handover types
  const handoverTypes = [
    { value: "ALL", label: "All", description: "Show both pending and handed over records" },
    { value: "HANDED_OVER", label: "Handed Over", description: "Show only completed handovers" },
    { value: "PENDING", label: "Pending", description: "Show only pending handovers" }
  ];

  // Filter handover types based on search term
  const filteredTypes = handoverTypes.filter(type =>
    type.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    type.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled && !loading) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm("");
        setHighlightedIndex(-1);
      }
    }
  };

  const handleSelect = (type) => {
    onChange(type.value);
    setIsOpen(false);
    setSearchTerm("");
    setHighlightedIndex(-1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setHighlightedIndex(-1); // Reset highlight when filtering
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev < filteredTypes.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev > 0 ? prev - 1 : filteredTypes.length - 1
      );
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(filteredTypes[highlightedIndex]);
    }
  };

  const selectedType = handoverTypes.find(type => type.value === value);

  return (
    <div className="position-relative" ref={dropdownRef}>
      {/* Error message display */}
      {error && errorMessage && (
        <div className="text-danger small mb-1 d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-1" style={{ fontSize: "12px" }}></i>
          {errorMessage}
        </div>
      )}
      
      <div
        className={`searchable-dropdown-main form-control form-control-sm ${isOpen ? "show" : ""} ${error ? "border-danger" : ""} ${loading ? "searchable-dropdown-loading" : ""}`}
        onClick={handleToggle}
        style={{
          cursor: disabled || loading ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: disabled ? "#e9ecef" : loading ? "#f8f9fa" : "#fff",
          opacity: disabled ? 0.65 : loading ? 0.8 : 1,
          height: "38px",
          position: "relative",
          paddingRight: "40px",
          paddingLeft: "12px",
          fontSize: "0.875rem",
          lineHeight: "1.5",
          borderRadius: "0.375rem",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
        }}
      >
        <span style={{ 
          color: selectedType ? "#000" : loading ? "#6c757d" : "#6c757d",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          fontWeight: selectedType ? "400" : "400"
        }}>
          {loading ? (
            <span className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2" style={{ width: "12px", height: "12px" }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Loading...
            </span>
          ) : selectedType ? selectedType.label : placeholder}
        </span>
        
        {/* Enhanced chevron with loading state */}
        <div style={{ 
          position: "absolute",
          right: "12px",
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          display: "flex",
          alignItems: "center",
          gap: "8px"
        }}>
          {/* Required indicator */}
          {required && !selectedType && (
            <span style={{ color: "#dc3545", fontSize: "12px", fontWeight: "bold" }}>*</span>
          )}
          
          {/* Chevron icon */}
          <i className={`searchable-dropdown-chevron fas fa-chevron-down ${isOpen ? "rotate-180" : ""}`} 
             style={{ 
               fontSize: "12px",
               color: disabled ? "#adb5bd" : error ? "#dc3545" : "#6c757d",
               transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
             }}></i>
        </div>
      </div>

      {isOpen && (
        <div
          className="position-absolute w-100 border border-top-0 rounded-bottom dropdown-fade-in"
          style={{
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1050,
            backgroundColor: "#fff",
            boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15), 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
            maxHeight: maxHeight,
            borderRadius: "0 0 0.375rem 0.375rem",
            border: "1px solid #dee2e6",
            borderTop: "none",
            display: "flex",
            flexDirection: "column"
          }}
        >
          {/* Enhanced Search Input */}
          <div className="p-3 border-bottom" style={{ backgroundColor: "#f8f9fa" }}>
            <div className="position-relative">
              <input
                ref={searchInputRef}
                type="text"
                className="searchable-dropdown-input form-control form-control-sm"
                placeholder="Type to search handover types..."
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                style={{ 
                  fontSize: "0.875rem",
                  paddingLeft: "32px",
                  paddingRight: "8px",
                  borderRadius: "0.25rem",
                  border: "1px solid #ced4da",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                }}
              />
              <i className="fas fa-search position-absolute" 
                 style={{
                   left: "10px",
                   top: "50%",
                   transform: "translateY(-50%)",
                   color: "#6c757d",
                   fontSize: "13px",
                   transition: "color 0.2s ease"
                 }}></i>
              {/* Clear search button */}
              {searchTerm && (
                <button
                  type="button"
                  className="btn btn-sm position-absolute"
                  onClick={() => setSearchTerm("")}
                  style={{
                    right: "4px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    padding: "2px 6px",
                    fontSize: "10px",
                    lineHeight: "1",
                    backgroundColor: "transparent",
                    border: "none",
                    color: "#6c757d"
                  }}
                  onMouseEnter={(e) => e.target.style.color = "#dc3545"}
                  onMouseLeave={(e) => e.target.style.color = "#6c757d"}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>

          {/* Enhanced Options List */}
          <div style={{ 
            flex: 1,
            overflowY: "auto", 
            overflowX: "hidden",
            minHeight: 0
          }}>
            {filteredTypes.length > 0 ? (
              <>
                {/* Enhanced count display */}
                {showCount && (
                  <div className="px-3 py-2 small text-muted d-flex align-items-center justify-content-between" 
                       style={{ 
                         backgroundColor: "#f8f9fa", 
                         borderBottom: "1px solid #dee2e6",
                         fontWeight: "500"
                       }}>
                    <span>
                      <i className="fas fa-list me-1" style={{ fontSize: "10px" }}></i>
                      {searchTerm ? `Found ${filteredTypes.length} option${filteredTypes.length !== 1 ? 's' : ''}` : `All ${filteredTypes.length} handover types`}
                    </span>
                    {searchTerm && (
                      <button
                        type="button"
                        className="btn btn-sm p-0"
                        onClick={() => setSearchTerm("")}
                        style={{
                          fontSize: "10px",
                          lineHeight: "1",
                          backgroundColor: "transparent",
                          border: "none",
                          color: "#6c757d",
                          padding: "2px"
                        }}
                        onMouseEnter={(e) => e.target.style.color = "#007bff"}
                        onMouseLeave={(e) => e.target.style.color = "#6c757d"}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
                {filteredTypes.map((type, index) => {
                  const isSelected = value === type.value;
                  const isHighlighted = index === highlightedIndex;
                  
                  return (
                    <div
                      key={type.value}
                      className={`searchable-dropdown-item px-3 py-2 d-flex align-items-start ${
                        isSelected ? 'searchable-dropdown-selected text-white' : 
                        isHighlighted ? 'searchable-dropdown-highlighted' : ''
                      }`}
                      onClick={() => handleSelect(type)}
                      style={{
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        minHeight: "52px", // Increased for better touch targets and description
                        borderBottom: "1px solid #f1f3f4",
                        position: "relative",
                        fontWeight: isSelected ? "500" : "400",
                        flexDirection: "column",
                        justifyContent: "center"
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected && !isHighlighted) {
                          e.target.style.backgroundColor = "#f8f9fa";
                          e.target.style.transform = "translateX(2px)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected && !isHighlighted) {
                          e.target.style.backgroundColor = "transparent";
                          e.target.style.transform = "translateX(0)";
                        }
                      }}
                      onMouseMove={() => setHighlightedIndex(index)}
                    >
                      {/* Selection indicator */}
                      {isSelected && (
                        <div style={{
                          position: "absolute",
                          left: "0",
                          top: "0",
                          bottom: "0",
                          width: "3px",
                          backgroundColor: "#fff",
                          borderRadius: "0 2px 2px 0"
                        }}></div>
                      )}
                      
                      {/* Option content */}
                      <div className="d-flex align-items-center w-100" style={{ 
                        marginLeft: isSelected ? "8px" : "0"
                      }}>
                        <span className="flex-grow-1 fw-medium" style={{ 
                          lineHeight: "1.4",
                          fontSize: "0.875rem"
                        }}>
                          {type.label}
                        </span>
                        
                        {/* Selection checkmark */}
                        {isSelected && (
                          <div className="d-flex align-items-center" style={{ marginLeft: "8px" }}>
                            <i className="fas fa-check" style={{ 
                              fontSize: "12px",
                              color: "#fff",
                              backgroundColor: "rgba(255, 255, 255, 0.2)",
                              borderRadius: "50%",
                              padding: "4px",
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}></i>
                          </div>
                        )}
                      </div>
                      
                      {/* Description */}
                      <div style={{ 
                        marginLeft: isSelected ? "8px" : "0",
                        fontSize: "0.75rem",
                        opacity: isSelected ? "0.9" : "0.7",
                        marginTop: "2px",
                        lineHeight: "1.3"
                      }}>
                        {type.description}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="px-3 py-5 text-center text-muted" style={{ minHeight: "120px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <div className="mb-3" style={{ 
                  backgroundColor: "#f8f9fa", 
                  borderRadius: "50%", 
                  width: "48px", 
                  height: "48px", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center" 
                }}>
                  <i className="fas fa-search" style={{ 
                    opacity: 0.4, 
                    fontSize: "20px",
                    color: "#6c757d"
                  }}></i>
                </div>
                <div className="fw-medium mb-1" style={{ fontSize: "0.9rem" }}>
                  {searchTerm ? "No matching options" : "No options available"}
                </div>
                {searchTerm ? (
                  <div className="small text-muted">
                    Try adjusting your search terms or{' '}
                    <button
                      type="button"
                      className="btn btn-link p-0 small"
                      onClick={() => setSearchTerm("")}
                      style={{ 
                        color: "#007bff", 
                        textDecoration: "none",
                        fontSize: "inherit"
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                      onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                    >
                      clear the search
                    </button>
                  </div>
                ) : (
                  <div className="small text-muted">
                    Options will appear here when available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableHandoverTypeDropdown;
